import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MoveDown } from "lucide-react";
import { smoothScrollTo } from "../SmoothScroll";
import { openWhatsApp } from "../../lib/whatsapp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770644426789-4fe022415ab7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxzcGljZSUyMGZhcm0lMjBncmVlbmVyeXxlbnwwfHx8fDE3ODU0OTM3NTJ8MA&ixlib=rb-4.1.0&q=85";

const POD_IMG =
  "https://images.unsplash.com/photo-1701166627787-12d9fdd437cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGNhcmRhbW9tJTIwcG9kc3xlbnwwfHx8fDE3ODU0OTM3NTJ8MA&ixlib=rb-4.1.0&q=85";

const HEADLINE_LINES = [
  { text: "Whole,", accent: false },
  { text: "hand-picked", accent: true },
  { text: "green cardamom.", accent: false },
];

const lineParent = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.15 },
  },
};

const lineChild = {
  hidden: { y: "115%", opacity: 0 },
  visible: {
    y: "0%",
    opacity: 1,
    transition: { duration: 1.1, ease: [0.65, 0, 0.35, 1] },
  },
};

export const Hero = ({ splashDone = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const podY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const podRotate = useTransform(scrollYProgress, [0, 1], [0, -6]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-forestDeep"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Cardamom plantation greenery"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forestDeep/70 via-forestDeep/55 to-forestDeep/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(201,162,39,0.18),transparent_55%)]" />
      </motion.div>

      {/* Signature 3D floating pod — top right */}
      <motion.div
        style={{ y: podY, rotate: podRotate }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.1, duration: 1.4, ease: [0.65, 0, 0.35, 1] }}
        className="pointer-events-none absolute -right-10 md:right-6 top-[26%] md:top-[22%] w-[240px] md:w-[360px] lg:w-[420px] aspect-[3/4] hidden sm:block will-change-transform"
      >
        <div className="relative h-full w-full rounded-[999px] overflow-hidden shadow-[0_60px_120px_rgba(0,0,0,0.45)] ring-1 ring-gold/40">
          <img
            src={POD_IMG}
            alt="Green cardamom pods"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forestDeep/40 via-transparent to-transparent" />
        </div>
        {/* orbiting gold ring */}
        <motion.svg
          viewBox="0 0 200 200"
          className="absolute -inset-6"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 60, ease: "linear" }}
        >
          <circle
            cx="100"
            cy="100"
            r="98"
            fill="none"
            stroke="#C9A227"
            strokeWidth="0.5"
            strokeDasharray="1 6"
            opacity="0.6"
          />
        </motion.svg>
      </motion.div>

      {/* Top meta row */}
      <div className="relative z-10 pt-28 md:pt-32 px-6 md:px-10">
        <div className="mx-auto max-w-[1400px] flex items-center justify-between text-cream/80 text-[11px] uppercase tracking-[0.32em]">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="hidden md:inline-flex items-center gap-2"
          >
            <span className="inline-block h-[1px] w-8 bg-cream/40" />
            Est. Ongole, AP
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="font-serif italic normal-case tracking-normal text-cream/70 text-sm"
          >
            Elettaria&nbsp;cardamomum · Vol. 01
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="hidden md:inline-flex items-center gap-2"
          >
            Sourced &amp; Exported
            <span className="inline-block h-[1px] w-8 bg-cream/40" />
          </motion.span>
        </div>
      </div>

      {/* Kinetic headline */}
      <motion.div
        style={{ y: titleY }}
        className="relative z-10 px-6 md:px-10 pt-16 md:pt-24 pb-32 md:pb-40"
      >
        <div className="mx-auto max-w-[1400px]">
          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate={splashDone ? "visible" : "hidden"}
            className="font-serif text-cream leading-[0.95] tracking-tight text-[16vw] sm:text-[13vw] md:text-[10.5vw] lg:text-[9.5vw] xl:text-[8.75rem]"
            data-testid="hero-headline"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  variants={lineChild}
                  className={`block ${
                    line.accent ? "italic text-gold font-light" : "font-light"
                  }`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.9 }}
              className="md:col-span-6 lg:col-span-5 font-sans text-cream/85 text-base md:text-lg leading-relaxed max-w-[46ch]"
              data-testid="hero-subheadline"
            >
              A small trading house from Ongole, Prakasam — moving farm-fresh
              Malabar &amp; Idukki cardamom to importers who care about aroma,
              oil content and the story behind each pod.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 0.9 }}
              className="md:col-span-6 lg:col-span-7 flex flex-wrap items-center gap-4 md:justify-end"
            >
              <button
                onClick={() =>
                  openWhatsApp(
                    "Hello, I'd like to enquire about premium green cardamom from The Cardamom Trading Co."
                  )
                }
                data-testid="hero-enquire-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-gold text-forestDeep px-7 py-4 text-[12px] uppercase tracking-[0.28em] font-medium hover:bg-cream transition-colors"
              >
                Enquire Now
                <ArrowDownRight
                  size={16}
                  className="transition-transform duration-500 group-hover:rotate-[-45deg]"
                />
              </button>

              <button
                onClick={() => smoothScrollTo("#about")}
                data-testid="hero-learn-more-cta"
                className="group inline-flex items-center gap-3 rounded-full border border-cream/40 text-cream px-7 py-4 text-[12px] uppercase tracking-[0.28em] hover:border-gold hover:text-gold transition-colors"
              >
                Learn More
                <MoveDown
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-y-1"
                />
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Bottom rail — running numbers */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-8 left-0 right-0 px-6 md:px-10 z-10"
      >
        <div className="mx-auto max-w-[1400px] flex items-end justify-between text-cream/70 font-sans text-[11px] uppercase tracking-[0.32em]">
          <div className="flex items-center gap-3">
            <span className="text-gold">01</span>
            <span className="hidden sm:inline">Sourced from the hills</span>
          </div>
          <div className="hidden md:flex items-center gap-6 font-serif italic normal-case tracking-normal text-cream/60">
            <span>Aroma · 8-10% oil</span>
            <span>·</span>
            <span>Bold 7mm+ pods</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline">Scroll</span>
            <MoveDown size={14} className="text-gold" />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
